import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";
import { STUDENT_LEVELS } from "../utils";
import { getFeeAmount, FEE_TYPES } from "./feeConfigService";
import { logActivity } from "./activityService";

// Payment Status
export const PAYMENT_STATUS = {
  PENDING: "pending",
  PARTIALLY_PAID: "partially_paid",
  FULLY_PAID: "fully_paid",
  OVERDUE: "overdue",
  CANCELLED: "cancelled",
};

// Payment Methods
export const PAYMENT_METHODS = {
  CASH: "cash",
  BANK_TRANSFER: "bank_transfer",
  UPI: "upi",
  CREDIT_CARD: "credit_card",
  DEBIT_CARD: "debit_card",
  CHEQUE: "cheque",
  OTHER: "other",
};

// Installment Constants
export const MAX_INSTALLMENTS = 2;

const STUDENT_FEES_COLLECTION = "studentFees";
const STUDENT_PAYMENTS_COLLECTION = "studentPayments";

// Initialize fee for a student (when they become paid_trainee or intern)
export const initializeStudentFee = async (studentId, studentData) => {
  try {
    // Check if fee already exists
    const q = query(
      collection(db, STUDENT_FEES_COLLECTION),
      where("studentId", "==", studentId)
    );
    const existingFees = await getDocs(q);

    if (!existingFees.empty) {
      console.log("Fee record already exists for student:", studentId);
      return existingFees.docs[0].id;
    }

    // Determine fee amount based on student level
    let totalFee = 0;
    let feeType = null;

    if (studentData.studentLevel === STUDENT_LEVELS.PAID_TRAINEE) {
      totalFee = await getFeeAmount(FEE_TYPES.PAID_TRAINEE);
      feeType = FEE_TYPES.PAID_TRAINEE;
    } else if (studentData.studentLevel === STUDENT_LEVELS.INTERN && !studentData.collegeId) {
      totalFee = await getFeeAmount(FEE_TYPES.INDIVIDUAL_INTERNSHIP);
      feeType = FEE_TYPES.INDIVIDUAL_INTERNSHIP;
    } else {
      // For other student levels or college interns, no fee initialization
      return null;
    }

    // Calculate installment amount
    const installmentAmount = totalFee / MAX_INSTALLMENTS;

    const feeRecord = {
      studentId: studentId,
      studentName: studentData.name,
      studentEmail: studentData.email,
      studentLevel: studentData.studentLevel,
      feeType: feeType,
      originalFee: totalFee,
      discountAmount: 0,
      discountPercentage: 0,
      discountReason: "",
      totalFee: totalFee,
      paidAmount: 0,
      remainingAmount: totalFee,
      installmentAmount: installmentAmount,
      maxInstallments: MAX_INSTALLMENTS,
      installmentsPaid: 0,
      installmentsRemaining: MAX_INSTALLMENTS,
      paymentStatus: PAYMENT_STATUS.PENDING,
      dueDate: calculateDueDate(1), // First installment due in 30 days
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, STUDENT_FEES_COLLECTION), feeRecord);

    // Log activity
    await logActivity({
      userId: studentId,
      text: `Fee initialized for ${studentData.name}: ₹${totalFee} (${MAX_INSTALLMENTS} installments)`,
      type: "fee",
      actionType: "admin_action",
      metadata: {
        feeId: docRef.id,
        totalFee: totalFee,
        studentLevel: studentData.studentLevel,
        action: "initialize"
      }
    });

    console.log("Student fee initialized:", docRef.id);
    return docRef.id;
  } catch (error) {
    console.error("Error initializing student fee:", error);
    toast.error("Failed to initialize student fee");
    throw error;
  }
};

// Calculate due date for installment (days from now)
const calculateDueDate = (installmentNumber) => {
  const daysFromNow = 30 * installmentNumber; // 30 days per installment
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + daysFromNow);
  return Timestamp.fromDate(dueDate);
};

// Get fee record for a student
export const getStudentFeeRecord = async (studentId) => {
  try {
    const q = query(
      collection(db, STUDENT_FEES_COLLECTION),
      where("studentId", "==", studentId)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const feeData = snapshot.docs[0].data();
    return {
      id: snapshot.docs[0].id,
      ...feeData,
      dueDate: feeData.dueDate?.toDate(),
      createdAt: feeData.createdAt?.toDate(),
      updatedAt: feeData.updatedAt?.toDate(),
    };
  } catch (error) {
    console.error("Error fetching student fee record:", error);
    return null;
  }
};

// Get payment history for a student
export const getStudentPaymentHistory = async (studentId) => {
  try {
    const q = query(
      collection(db, STUDENT_PAYMENTS_COLLECTION),
      where("studentId", "==", studentId),
      orderBy("paymentDate", "desc")
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        paymentDate: data.paymentDate?.toDate(),
        createdAt: data.createdAt?.toDate(),
      };
    });
  } catch (error) {
    console.error("Error fetching payment history:", error);
    return [];
  }
};

// Record a payment
export const recordPayment = async (paymentData) => {
  try {
    const {
      studentId,
      feeId,
      amount,
      paymentMethod,
      transactionId,
      notes,
      receivedBy,
    } = paymentData;

    // Validate amount
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error("Invalid payment amount");
      return null;
    }

    // Get current fee record
    const feeDoc = doc(db, STUDENT_FEES_COLLECTION, feeId);
    const feeSnapshot = await getDoc(feeDoc);

    if (!feeSnapshot.exists()) {
      toast.error("Fee record not found");
      return null;
    }

    const feeData = feeSnapshot.data();

    // Check if payment exceeds remaining amount
    if (parsedAmount > feeData.remainingAmount) {
      toast.error(`Payment amount cannot exceed remaining amount: ₹${feeData.remainingAmount}`);
      return null;
    }

    // Create payment record
    const payment = {
      studentId: studentId,
      studentName: feeData.studentName,
      studentEmail: feeData.studentEmail,
      feeId: feeId,
      amount: parsedAmount,
      paymentMethod: paymentMethod,
      transactionId: transactionId || `TXN-${Date.now()}`,
      installmentNumber: feeData.installmentsPaid + 1,
      notes: notes || "",
      receivedBy: receivedBy || "system",
      paymentDate: serverTimestamp(),
      createdAt: serverTimestamp(),
    };

    const paymentRef = await addDoc(collection(db, STUDENT_PAYMENTS_COLLECTION), payment);

    // Update fee record
    const updatedPaidAmount = feeData.paidAmount + parsedAmount;
    const updatedRemainingAmount = feeData.totalFee - updatedPaidAmount;
    const updatedInstallmentsPaid = feeData.installmentsPaid + 1;
    const updatedInstallmentsRemaining = feeData.maxInstallments - updatedInstallmentsPaid;

    let newPaymentStatus = PAYMENT_STATUS.PARTIALLY_PAID;
    if (updatedRemainingAmount === 0) {
      newPaymentStatus = PAYMENT_STATUS.FULLY_PAID;
    } else if (updatedRemainingAmount > 0 && updatedInstallmentsPaid === 0) {
      newPaymentStatus = PAYMENT_STATUS.PENDING;
    }

    // Calculate next due date if not fully paid
    let nextDueDate = feeData.dueDate;
    if (newPaymentStatus !== PAYMENT_STATUS.FULLY_PAID) {
      nextDueDate = calculateDueDate(updatedInstallmentsPaid + 1);
    }

    await updateDoc(feeDoc, {
      paidAmount: updatedPaidAmount,
      remainingAmount: updatedRemainingAmount,
      installmentsPaid: updatedInstallmentsPaid,
      installmentsRemaining: updatedInstallmentsRemaining,
      paymentStatus: newPaymentStatus,
      dueDate: nextDueDate,
      lastPaymentDate: serverTimestamp(),
      lastPaymentAmount: parsedAmount,
      updatedAt: serverTimestamp(),
    });

    // Log activity
    await logActivity({
      userId: studentId,
      text: `Payment received from ${feeData.studentName}: ₹${parsedAmount} (Installment ${updatedInstallmentsPaid}/${feeData.maxInstallments})`,
      type: "fee",
      actionType: "payment",
      metadata: {
        feeId: feeId,
        paymentId: paymentRef.id,
        amount: parsedAmount,
        installmentNumber: updatedInstallmentsPaid,
        remainingAmount: updatedRemainingAmount,
        paymentMethod: paymentMethod,
        action: "payment_received"
      }
    });

    toast.success(`Payment of ₹${parsedAmount} recorded successfully!`);
    return paymentRef.id;
  } catch (error) {
    console.error("Error recording payment:", error);
    toast.error("Failed to record payment");
    throw error;
  }
};

// Get all student fees (for admin)
export const getAllStudentFees = async (filters = {}) => {
  try {
    let q = collection(db, STUDENT_FEES_COLLECTION);

    // Apply filters
    const constraints = [];

    if (filters.paymentStatus) {
      constraints.push(where("paymentStatus", "==", filters.paymentStatus));
    }

    if (filters.studentLevel) {
      constraints.push(where("studentLevel", "==", filters.studentLevel));
    }

    if (constraints.length > 0) {
      q = query(q, ...constraints, orderBy("createdAt", "desc"));
    } else {
      q = query(q, orderBy("createdAt", "desc"));
    }

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        dueDate: data.dueDate?.toDate(),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        lastPaymentDate: data.lastPaymentDate?.toDate(),
      };
    });
  } catch (error) {
    console.error("Error fetching all student fees:", error);
    return [];
  }
};

// Get all payments (for admin)
export const getAllPayments = async (filters = {}) => {
  try {
    let q = collection(db, STUDENT_PAYMENTS_COLLECTION);

    if (filters.studentId) {
      q = query(q, where("studentId", "==", filters.studentId), orderBy("paymentDate", "desc"));
    } else {
      q = query(q, orderBy("paymentDate", "desc"));
    }

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        paymentDate: data.paymentDate?.toDate(),
        createdAt: data.createdAt?.toDate(),
      };
    });
  } catch (error) {
    console.error("Error fetching all payments:", error);
    return [];
  }
};

// Get fee statistics (for admin dashboard)
export const getFeeStatistics = async () => {
  try {
    const fees = await getAllStudentFees();
    const payments = await getAllPayments();

    const totalFees = fees.reduce((sum, fee) => sum + fee.totalFee, 0);
    const totalPaid = fees.reduce((sum, fee) => sum + fee.paidAmount, 0);
    const totalPending = fees.reduce((sum, fee) => sum + fee.remainingAmount, 0);

    const fullyPaidCount = fees.filter(fee => fee.paymentStatus === PAYMENT_STATUS.FULLY_PAID).length;
    const partiallyPaidCount = fees.filter(fee => fee.paymentStatus === PAYMENT_STATUS.PARTIALLY_PAID).length;
    const pendingCount = fees.filter(fee => fee.paymentStatus === PAYMENT_STATUS.PENDING).length;
    const overdueCount = fees.filter(fee => {
      if (fee.paymentStatus === PAYMENT_STATUS.FULLY_PAID) return false;
      const dueDate = fee.dueDate;
      return dueDate && dueDate < new Date();
    }).length;

    const totalPayments = payments.length;
    const recentPayments = payments.slice(0, 10);

    return {
      totalFees,
      totalPaid,
      totalPending,
      collectionRate: totalFees > 0 ? (totalPaid / totalFees) * 100 : 0,
      fullyPaidCount,
      partiallyPaidCount,
      pendingCount,
      overdueCount,
      totalPayments,
      recentPayments,
      totalStudents: fees.length,
    };
  } catch (error) {
    console.error("Error fetching fee statistics:", error);
    return null;
  }
};

// Update payment status (for admin)
export const updatePaymentStatus = async (feeId, newStatus) => {
  try {
    const feeDoc = doc(db, STUDENT_FEES_COLLECTION, feeId);
    await updateDoc(feeDoc, {
      paymentStatus: newStatus,
      updatedAt: serverTimestamp(),
    });

    toast.success("Payment status updated successfully");
  } catch (error) {
    console.error("Error updating payment status:", error);
    toast.error("Failed to update payment status");
    throw error;
  }
};

// Check and update overdue fees
export const checkOverdueFees = async () => {
  try {
    const fees = await getAllStudentFees();
    const currentDate = new Date();
    let overdueCount = 0;

    for (const fee of fees) {
      if (
        fee.paymentStatus !== PAYMENT_STATUS.FULLY_PAID &&
        fee.dueDate &&
        fee.dueDate < currentDate &&
        fee.paymentStatus !== PAYMENT_STATUS.OVERDUE
      ) {
        const feeDoc = doc(db, STUDENT_FEES_COLLECTION, fee.id);
        await updateDoc(feeDoc, {
          paymentStatus: PAYMENT_STATUS.OVERDUE,
          updatedAt: serverTimestamp(),
        });
        overdueCount++;
      }
    }

    if (overdueCount > 0) {
      console.log(`Updated ${overdueCount} fees to overdue status`);
    }

    return overdueCount;
  } catch (error) {
    console.error("Error checking overdue fees:", error);
    return 0;
  }
};

// Apply discount to fee record
export const applyDiscount = async (feeId, discountData) => {
  try {
    const { discountType, discountValue, discountReason } = discountData;

    // Get current fee record
    const feeDoc = doc(db, STUDENT_FEES_COLLECTION, feeId);
    const feeSnapshot = await getDoc(feeDoc);

    if (!feeSnapshot.exists()) {
      toast.error("Fee record not found");
      return null;
    }

    const feeData = feeSnapshot.data();
    const originalFee = feeData.originalFee || feeData.totalFee;

    // Validate that no payments have been made yet
    if (feeData.paidAmount > 0) {
      toast.error("Cannot apply discount after payments have been made");
      return null;
    }

    let discountAmount = 0;
    let discountPercentage = 0;

    if (discountType === "percentage") {
      // Percentage discount
      const percentage = parseFloat(discountValue);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) {
        toast.error("Invalid discount percentage (0-100)");
        return null;
      }
      discountPercentage = percentage;
      discountAmount = (originalFee * percentage) / 100;
    } else {
      // Fixed amount discount
      const amount = parseFloat(discountValue);
      if (isNaN(amount) || amount < 0 || amount >= originalFee) {
        toast.error("Invalid discount amount");
        return null;
      }
      discountAmount = amount;
      discountPercentage = (amount / originalFee) * 100;
    }

    // Calculate new totals
    const newTotalFee = originalFee - discountAmount;
    const newInstallmentAmount = newTotalFee / feeData.maxInstallments;

    // Update fee record
    await updateDoc(feeDoc, {
      originalFee: originalFee,
      discountAmount: discountAmount,
      discountPercentage: discountPercentage,
      discountReason: discountReason || "",
      totalFee: newTotalFee,
      remainingAmount: newTotalFee,
      installmentAmount: newInstallmentAmount,
      updatedAt: serverTimestamp(),
    });

    // Log activity
    await logActivity({
      userId: feeData.studentId,
      text: `Discount applied to ${feeData.studentName}: ${formatCurrency(discountAmount)} (${discountPercentage.toFixed(1)}%)`,
      type: "fee",
      actionType: "admin_action",
      metadata: {
        feeId: feeId,
        originalFee: originalFee,
        discountAmount: discountAmount,
        discountPercentage: discountPercentage,
        newTotalFee: newTotalFee,
        discountReason: discountReason,
        action: "discount_applied"
      }
    });

    toast.success(`Discount of ${formatCurrency(discountAmount)} applied successfully!`);
    return { discountAmount, discountPercentage, newTotalFee };
  } catch (error) {
    console.error("Error applying discount:", error);
    toast.error("Failed to apply discount");
    throw error;
  }
};

// Remove discount from fee record
export const removeDiscount = async (feeId) => {
  try {
    // Get current fee record
    const feeDoc = doc(db, STUDENT_FEES_COLLECTION, feeId);
    const feeSnapshot = await getDoc(feeDoc);

    if (!feeSnapshot.exists()) {
      toast.error("Fee record not found");
      return null;
    }

    const feeData = feeSnapshot.data();

    // Validate that no payments have been made yet
    if (feeData.paidAmount > 0) {
      toast.error("Cannot remove discount after payments have been made");
      return null;
    }

    const originalFee = feeData.originalFee || feeData.totalFee;
    const newInstallmentAmount = originalFee / feeData.maxInstallments;

    // Update fee record
    await updateDoc(feeDoc, {
      discountAmount: 0,
      discountPercentage: 0,
      discountReason: "",
      totalFee: originalFee,
      remainingAmount: originalFee,
      installmentAmount: newInstallmentAmount,
      updatedAt: serverTimestamp(),
    });

    // Log activity
    await logActivity({
      userId: feeData.studentId,
      text: `Discount removed from ${feeData.studentName}`,
      type: "fee",
      actionType: "admin_action",
      metadata: {
        feeId: feeId,
        previousDiscountAmount: feeData.discountAmount,
        restoredFee: originalFee,
        action: "discount_removed"
      }
    });

    toast.success("Discount removed successfully!");
    return { restoredFee: originalFee };
  } catch (error) {
    console.error("Error removing discount:", error);
    toast.error("Failed to remove discount");
    throw error;
  }
};

// Format currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

// Get payment status badge color
export const getPaymentStatusColor = (status) => {
  switch (status) {
    case PAYMENT_STATUS.FULLY_PAID:
      return "success";
    case PAYMENT_STATUS.PARTIALLY_PAID:
      return "info";
    case PAYMENT_STATUS.PENDING:
      return "warning";
    case PAYMENT_STATUS.OVERDUE:
      return "danger";
    case PAYMENT_STATUS.CANCELLED:
      return "secondary";
    default:
      return "secondary";
  }
};

// Get payment status display name
export const getPaymentStatusDisplay = (status) => {
  return status ? status.replace(/_/g, " ").toUpperCase() : "UNKNOWN";
};
